import { type FormEvent, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, Eye, EyeOff } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import Loader from "@/components/shared/Loader";
import { useToast } from "@/components/ui/use-toast";
import { completeBackendProfile, passwordSignupBackendUser, signInBackendOAuth, signInBackendUser, startBackendOtp, startPasswordSignupBackendUser, verifyBackendOtp } from "@/lib/backend/api";
import { useUserContext } from "@/context/AuthContext";
import SocialSignInButtons from "./SocialSignInButtons";

type Step = "phone" | "otp" | "profile" | "password";
type OtpPurpose = "login" | "password-signup";

const normalizePhoneInput = (value: string) => value.replace(/\D/g, "").slice(-10);
const isValidPhone = (value: string) => normalizePhoneInput(value).length === 10;

const OtpAuthForm = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { checkAuthUser } = useUserContext();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [step, setStep] = useState<Step>("phone");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [devOtp, setDevOtp] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [otpPurpose, setOtpPurpose] = useState<OtpPurpose>("login");
  const startRequestInFlight = useRef(false);
  const authMode = location.pathname.includes("/sign-up") ? "signup" : "signin";

  const redirectTo = searchParams.get("redirect") || "/home";
  const cleanPhone = normalizePhoneInput(phoneNumber);

  const handleStartOtp = async (event: FormEvent) => {
    event.preventDefault();
    setOtpPurpose("login");
    await sendOtp();
  };

  const sendOtp = async () => {
    if (startRequestInFlight.current || !isValidPhone(phoneNumber)) return;

    startRequestInFlight.current = true;
    setIsLoading(true);
    try {
      const response = await startBackendOtp(cleanPhone);
      setDevOtp(response.devOtp ?? null);
      setStep("otp");
      toast({
        title: "OTP sent",
        description: response.devOtp ? `Dev OTP: ${response.devOtp}` : "Check your phone for the verification code.",
      });
    } catch (error) {
      toast({
        title: "Could not send OTP",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      startRequestInFlight.current = false;
    }
  };

  const handleVerifyOtp = async (event: FormEvent) => {
    event.preventDefault();
    if (otp.trim().length !== 6) return;

    setIsLoading(true);
    try {
      const response =
        otpPurpose === "password-signup"
          ? await passwordSignupBackendUser(cleanPhone, password, otp.trim(), navigator.userAgent.slice(0, 120))
          : await verifyBackendOtp(cleanPhone, otp.trim());
      if (response.profileRequired) {
        setStep("profile");
        return;
      }

      await checkAuthUser();
      navigate(redirectTo);
    } catch (error) {
      toast({
        title: "OTP verification failed",
        description: error instanceof Error ? error.message : "Please check the OTP and try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCompleteProfile = async (event: FormEvent) => {
    event.preventDefault();
    if (displayName.trim().length < 2) return;

    setIsLoading(true);
    try {
      await completeBackendProfile(displayName.trim());
      await checkAuthUser();
      navigate(redirectTo);
    } catch (error) {
      toast({
        title: "Profile setup failed",
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordLogin = async (event: FormEvent) => {
    event.preventDefault();
    if (!isValidPhone(phoneNumber) || password.length < 8) return;

    setIsLoading(true);
    try {
      await signInBackendUser(cleanPhone, password, navigator.userAgent.slice(0, 120));
      await checkAuthUser();
      navigate(redirectTo);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Please check your phone and password.";
      if (message.includes("OTP login") || message.includes("set a password first")) {
        setStep("phone");
        setPassword("");
      }
      toast({
        title: "Could not login",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSignup = async () => {
    if (startRequestInFlight.current || !isValidPhone(phoneNumber) || password.length < 8) return;

    setOtpPurpose("password-signup");
    setOtp("");
    startRequestInFlight.current = true;
    setIsLoading(true);
    try {
      const response = await startPasswordSignupBackendUser(cleanPhone, password);
      setDevOtp(response.devOtp ?? null);
      setStep("otp");
      toast({
        title: "OTP sent",
        description: response.devOtp ? `Dev OTP: ${response.devOtp}` : "Check your phone for the verification code.",
      });
    } catch (error) {
      toast({
        title: "Account already exists",
        description: error instanceof Error ? error.message : "Please login instead.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
      startRequestInFlight.current = false;
    }
  };

  const handleSocialSignIn = async (provider: "google" | "apple", token: string) => {
    setIsLoading(true);
    try {
      await signInBackendOAuth(provider, token, navigator.userAgent.slice(0, 120));
      await checkAuthUser();
      navigate(redirectTo);
    } catch (error) {
      toast({
        title: `${provider === "google" ? "Google" : "Apple"} sign-in failed`,
        description: error instanceof Error ? error.message : "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSocialSignInError = (error: Error) => {
    toast({
      title: "Apple sign-in failed",
      description: error.message,
      variant: "destructive",
    });
  };

  return (
    <div className="flex-center flex-col px-4 py-8 w-full max-w-md mx-auto">
      <img className="w-40 mb-6" src="/assets/images/busiconn.svg" alt="busiconn" />

      <div className="inline-flex items-center gap-2 rounded-full border border-primary-500/20 bg-primary-500/10 px-3 py-1 text-[11px] font-medium text-primary-300">
        <span className="h-2 w-2 rounded-full bg-primary-400 animate-pulse" />
        {authMode === "signup" ? "Create your Busiconn account" : "Welcome to your platform!"}
      </div>

      <h2 className="h3-bold md:h2-bold text-center mt-2">
        {step === "profile"
          ? "Finish your profile"
          : step === "otp"
            ? "Verify your mobile"
            : step === "password"
              ? authMode === "signup"
                ? "Create a password"
                : "Login with password"
              : authMode === "signup"
                ? "Start buying and selling"
                : "Continue to Busiconn"}
      </h2>
      <p className="text-light-3 small-medium md:base-regular mt-2 text-center">
        {step === "phone" && "Use your mobile number to continue."}
        {step === "otp" && `Enter the 6 digit code sent to ${cleanPhone}.`}
        {step === "profile" && "This is the last step to activate your profile."}
        {step === "password" && (authMode === "signup" ? "Set a password for faster future login." : "Use your mobile number and password.")}
      </p>

      <p className="text-[11px] text-light-4 text-center mt-2">
        For buyers, sellers, freelancers, and resellers. Connect, agree on a deal, and pay with protection.
      </p>

      {step === "phone" && (
        <form onSubmit={handleStartOtp} className="flex flex-col gap-5 w-full mt-6">
          <div>
            <label className="shad-form_label">Phone Number</label>
            <Input
              type="tel"
              inputMode="numeric"
              placeholder="Your 10 digit mobile number"
              className="shad-input mt-2"
              value={phoneNumber}
              onChange={(event) => setPhoneNumber(event.target.value)}
            />
          </div>

          <Button type="submit" className="shad-button_primary" disabled={isLoading || !isValidPhone(phoneNumber)}>
            {isLoading ? <Loader /> : authMode === "signup" ? "Continue" : "Continue"}
          </Button>

          <button
            type="button"
            className="text-small-regular text-light-3 text-center"
            onClick={() => setStep("password")}
          >
            {authMode === "signup" ? "Create with password instead" : "Login using password"}
          </button>

          <SocialSignInButtons disabled={isLoading} onToken={handleSocialSignIn} onError={handleSocialSignInError} />
        </form>
      )}

      {step === "password" && (
        <form onSubmit={handlePasswordLogin} className="flex flex-col gap-5 w-full mt-6">
          <Button type="button" variant="ghost" className="justify-start px-0" onClick={() => setStep("phone")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to OTP login
          </Button>

          <div>
            <label className="shad-form_label">Phone Number</label>
            <Input
              type="tel"
              inputMode="numeric"
              placeholder="Your 10 digit mobile number"
              className="shad-input mt-2"
              value={phoneNumber}
              onChange={(event) => setPhoneNumber(event.target.value)}
            />
          </div>

          <div>
            <label className="shad-form_label">Password</label>
            <div className="relative mt-2">
              <Input
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                className="shad-input pr-10"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                onClick={() => setShowPassword(!showPassword)}
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff className="h-4 w-4 text-muted-foreground" /> : <Eye className="h-4 w-4 text-muted-foreground" />}
              </Button>
            </div>
          </div>

          <Button type="submit" className="shad-button_primary" disabled={isLoading || !isValidPhone(phoneNumber) || password.length < 8}>
            {isLoading ? <Loader /> : "Login"}
          </Button>

          <Button type="button" variant="secondary" onClick={handlePasswordSignup} disabled={isLoading || !isValidPhone(phoneNumber) || password.length < 8}>
            Create account with password
          </Button>

          {/* Forgot password will be added after the OTP reset flow is finalized.
          <button
            type="button"
            className="text-small-regular text-light-3 text-center"
            onClick={() =>
              toast({
                title: "Forgot password coming next",
                description: "For now, login with OTP and set a new password from profile/settings.",
              })
            }
          >
            Forgot Password?
          </button>
          */}
        </form>
      )}

      {step === "otp" && (
        <form onSubmit={handleVerifyOtp} className="flex flex-col gap-5 w-full mt-6">
          <Button type="button" variant="ghost" className="justify-start px-0" onClick={() => setStep(otpPurpose === "password-signup" ? "password" : "phone")}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {otpPurpose === "password-signup" ? "Back to password signup" : "Change phone number"}
          </Button>

          <div>
            <label className="shad-form_label">Verification Code</label>
            <Input
              type="text"
              inputMode="numeric"
              maxLength={6}
              placeholder="000000"
              className="shad-input mt-2 tracking-[0.5em] text-center"
              value={otp}
              onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))}
            />
            {devOtp && <p className="text-xs text-light-3 mt-2">Local dev OTP: {devOtp}</p>}
          </div>

          <Button type="submit" className="shad-button_primary" disabled={isLoading || otp.length !== 6}>
            {isLoading ? <Loader /> : "Verify and Continue"}
          </Button>

          <Button type="button" variant="secondary" onClick={sendOtp} disabled={isLoading}>
            Resend OTP
          </Button>
        </form>
      )}

      {step === "profile" && (
        <form onSubmit={handleCompleteProfile} className="flex flex-col gap-5 w-full mt-6">
          <div>
            <label className="shad-form_label">Display Name</label>
            <Input
              type="text"
              placeholder="Sharma Electronics, Aman Verma..."
              className="shad-input mt-2"
              value={displayName}
              onChange={(event) => setDisplayName(event.target.value)}
            />
          </div>

          <Button type="submit" className="shad-button_primary" disabled={isLoading || displayName.trim().length < 2}>
            {isLoading ? <Loader /> : "Start using Busiconn"}
          </Button>
        </form>
      )}

      <p className="text-xs text-light-3 text-center mt-6">
        By continuing, you agree to our{" "}
        <Link to="/termsandconditions" className="text-primary-500 underline">
          Terms
        </Link>{" "}
        and{" "}
        <Link to="/privacy-policy" className="text-primary-500 underline">
          Privacy Policy
        </Link>
        .
      </p>
    </div>
  );
};

export default OtpAuthForm;
